import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X as XIcon, Compass, Lightbulb, ArrowRight } from "lucide-react";

/**
 * HomeOnboardingDialog — UNICO popup di onboarding della Home.
 *
 * Scopo: insegnare all'utente DOVE trovare il Centro Guida
 * (Innovazione → TIME Watch → pulsante "Guida" in alto a destra)
 * senza aprirlo direttamente.
 *
 * Persistenza: la scelta "Non visualizzare più questo messaggio"
 * viene salvata come user-preference (cloud + localStorage) tramite
 * `useUserPref("homeOnboardingDismissed", …)` gestita dal chiamante.
 */
export default function HomeOnboardingDialog({
  open,
  onClose,
  onDontShowAgain,
}) {
  const navigate = useNavigate();
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    if (open) setDontShow(false);
  }, [open]);

  if (!open) return null;

  const commitPref = () => {
    if (dontShow && typeof onDontShowAgain === "function") {
      onDontShowAgain(true);
    }
  };

  const handleClose = () => {
    commitPref();
    onClose && onClose();
  };

  const handleGoInnovazione = () => {
    commitPref();
    onClose && onClose();
    navigate("/innovazione");
  };

  return (
    <div
      data-testid="home-onboarding-backdrop"
      className="fixed inset-0 z-[65] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="home-onboarding-title"
    >
      <div
        className="glass-card w-full max-w-lg p-7 sm:p-9 relative border-emerald-400/25 shadow-[0_25px_80px_-20px_rgba(16,185,129,0.35)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="home-onboarding-dialog"
      >
        <button
          type="button"
          onClick={handleClose}
          data-testid="home-onboarding-close"
          aria-label="Chiudi"
          className="absolute top-4 right-4 p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/[0.06] transition"
        >
          <XIcon className="w-4 h-4" />
        </button>

        <div className="text-white/90">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-emerald-300/85 mb-3">
            <Compass className="w-3 h-3" aria-hidden="true" />
            TIME Inside · Onboarding
          </div>

          <h2
            id="home-onboarding-title"
            data-testid="home-onboarding-title"
            className="text-2xl sm:text-3xl font-extralight text-white mb-5 leading-tight"
          >
            Benvenuto in TIME Inside.
          </h2>

          <div className="space-y-4 text-sm sm:text-[15px] text-white/70 font-light leading-relaxed">
            <p>
              Per avere tutte le informazioni di cui hai bisogno, consulta il{" "}
              <span className="text-white/90">Centro Guida</span>.
            </p>
            <p>
              Vai in{" "}
              <span className="text-emerald-300/95">
                Innovazione → TIME Watch
              </span>
              , entra nella{" "}
              <span className="text-white/90">modalità TIME Watch</span> e in
              alto a destra troverai il{" "}
              <span className="text-white/90">Centro Guida</span>.
            </p>
          </div>

          <label
            className="flex items-center gap-2.5 mt-7 mb-6 select-none cursor-pointer group"
            data-testid="home-onboarding-dontshow-label"
          >
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              data-testid="home-onboarding-dontshow"
              className="w-4 h-4 rounded border-white/25 bg-black/40 text-emerald-400 focus:ring-emerald-400/40 focus:ring-offset-0"
            />
            <span className="text-[13px] text-white/60 group-hover:text-white/80 transition-colors">
              Non visualizzare più questo messaggio
            </span>
          </label>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleGoInnovazione}
              data-testid="home-onboarding-go-innovazione"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-400 text-black text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-emerald-300 transition-colors"
            >
              <Lightbulb className="w-4 h-4" aria-hidden="true" />
              Vai a Innovazione
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleClose}
              data-testid="home-onboarding-later"
              className="flex-1 inline-flex items-center justify-center px-5 py-3 rounded-full border border-white/15 text-white/80 text-[11px] tracking-[0.28em] uppercase hover:bg-white/[0.06] hover:text-white transition-colors"
            >
              Più tardi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
